/**
 * DiagramInfoPanel Component (Issue #156)
 *
 * Displays diagram metadata including description, methodology,
 * data sources, and interpretation guide in a slide-out panel.
 *
 * Issue #404: Enhanced for E2E flow diagrams with stats, logic, and evidence.
 */

import { useMemo, useState } from 'react';
import { diagramDescriptions, type DataSource, type E2EFlowPath, type FlowStepType } from '@veodiagram/shared';

interface DiagramMetadata {
  description?: string | null;
  methodology?: string | null;
  dataSources?: DataSource[] | null;
  generatedBy?: string | null;
  confidence?: number | null;
  interpretationGuide?: string | null;
}

// Issue #404: E2E flow specific stats
interface E2EFlowStats {
  totalPaths: number;
  completePaths: number;
  incompletePaths: number;
  totalSteps: number;
  stepsByType: Partial<Record<FlowStepType, number>>;
}

// Issue #404: E2E flow specific props
interface E2EFlowInfoProps {
  flow?: E2EFlowPath | null;
  stats?: E2EFlowStats | null;
}

// Issue #411: AI Enhancement info for transparency
interface AIEnhancementInfo {
  /** Whether the flow has been AI-enhanced */
  enhanced: boolean;
  /** When the enhancement was applied */
  enhancedAt?: string;
  /** Depth of enhancement */
  depth?: 'standard' | 'deep' | 'ultra-deep';
  /** Whether verification loop was used */
  verificationUsed?: boolean;
  /** Number of verification questions asked */
  questionsAsked?: number;
  /** Details of verification questions and answers */
  verificationDetails?: Array<{
    question: string;
    questionType: string;
    answer: string;
    confidence: number;
  }>;
  /** What changed during verification */
  verificationRevisions?: Array<{
    type: 'name' | 'description' | 'missingStepRemoved' | 'missingStepAdded' | 'stepLabelChanged';
    original?: string;
    revised?: string;
    reason: string;
  }>;
  /** How many enhancements were accepted */
  acceptedCount?: number;
  /** How many enhancements were rejected */
  rejectedCount?: number;
  /** Issue #424: Full prompt sent to AI */
  promptSent?: string;
  /** Issue #424: Raw AI response */
  aiResponse?: string;
}

interface DiagramInfoPanelProps {
  diagramType?: string | null;
  metadata?: DiagramMetadata;
  isOpen: boolean;
  onClose: () => void;
  nodeCount?: number;
  edgeCount?: number;
  createdAt?: string;
  updatedAt?: string;
  // Issue #404: E2E flow specific props
  e2eFlowInfo?: E2EFlowInfoProps;
  // Issue #411: AI Enhancement info for transparency
  aiEnhancementInfo?: AIEnhancementInfo;
}

// Issue #404: Step type labels for display
const STEP_TYPE_LABELS: Record<FlowStepType, string> = {
  'user-action': 'User Action',
  'ui-component': 'UI Component',
  'api-call': 'API Call',
  'api-route': 'API Route',
  'backend-logic': 'Backend Logic',
  'service-call': 'Service Call',
  'database': 'Database',
  'external-api': 'External API',
  'state-change': 'State Change',
  'navigation': 'Navigation',
  'navigation-target': 'Navigation Target',
  'modal-container': 'Modal Container',
  'modal-outcome': 'Modal Outcome',
  'prop-callback': 'Prop Callback',
  'api-response': 'API Response',
  'api-callback': 'API Callback',
  'dom-update': 'DOM Update',
  'error': 'Error',
  'cache': 'Cache',
  'middleware': 'Middleware',
  'pre-action-gate': 'Pre-Action Gate',
};

export function DiagramInfoPanel({
  diagramType,
  metadata,
  isOpen,
  onClose,
  nodeCount = 0,
  edgeCount = 0,
  createdAt,
  updatedAt,
  e2eFlowInfo,
  aiEnhancementInfo,
}: DiagramInfoPanelProps) {
  // Issue #404: Check if this is an E2E flow diagram
  const isE2EFlow = diagramType === 'e2eFlow' || diagramType === 'e2e-flow';
  // Issue #424: State for showing AI debug info (prompt/response)
  const [showAiDebug, setShowAiDebug] = useState(false);
  // Get static description for this diagram type
  const staticDescription = useMemo(() => {
    if (!diagramType) return null;
    // Convert diagram type to key format (e.g., 'container-architecture' -> 'containerArchitecture')
    const key = diagramType.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase());
    return diagramDescriptions[key] || diagramDescriptions[diagramType] || null;
  }, [diagramType]);

  // Use metadata from diagram if available, otherwise fall back to static descriptions
  const description = metadata?.description || staticDescription?.description || 'No description available';
  const methodology = metadata?.methodology || staticDescription?.methodology || 'Analysis method not specified';
  const interpretationGuide = metadata?.interpretationGuide || staticDescription?.interpretationGuide;
  const dataSources = metadata?.dataSources || staticDescription?.typicalDataSources || [];
  const legend = staticDescription?.defaultLegend || [];
  const generatedBy = metadata?.generatedBy || 'unknown';
  const confidence = metadata?.confidence;

  // Format generation method
  const generationMethod = useMemo(() => {
    switch (generatedBy) {
      case 'rule-based':
        return 'Rule-based Analysis';
      case 'ai':
        return 'AI-powered Analysis';
      case 'hybrid':
        return 'Hybrid (Rules + AI)';
      case 'manual':
        return 'Manual Creation';
      default:
        return 'Automatic Analysis';
    }
  }, [generatedBy]);

  // Format date
  const formatDate = (dateStr?: string) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/20 z-40"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="fixed right-0 top-0 h-full w-96 max-w-full bg-white dark:bg-slate-900 shadow-xl z-50 overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-700">
          <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200 flex items-center gap-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            About This Diagram
          </h3>
          <button
            onClick={onClose}
            className="p-1 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-6">
          {/* Title and type */}
          {staticDescription && (
            <div>
              <h4 className="text-base font-medium text-slate-800 dark:text-slate-200">
                {staticDescription.title}
              </h4>
              <div className="flex items-center gap-2 mt-1">
                <span className="px-2 py-0.5 text-xs font-medium bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 rounded">
                  {staticDescription.category}
                </span>
                <span className="px-2 py-0.5 text-xs font-medium bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 rounded">
                  {staticDescription.complexity}
                </span>
              </div>
            </div>
          )}

          {/* Description */}
          <div>
            <h5 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              Description
            </h5>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              {description}
            </p>
          </div>

          {/* Stats - Issue #404: Show E2E-specific stats or default stats */}
          {isE2EFlow && e2eFlowInfo?.stats ? (
            <>
              {/* E2E Flow Stats */}
              <div>
                <h5 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Flow Statistics
                </h5>
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                    <div className="text-2xl font-bold text-slate-800 dark:text-slate-200">
                      {e2eFlowInfo.stats.totalPaths}
                    </div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">
                      Paths ({e2eFlowInfo.stats.completePaths} complete)
                    </div>
                  </div>
                  <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                    <div className="text-2xl font-bold text-slate-800 dark:text-slate-200">
                      {e2eFlowInfo.stats.totalSteps}
                    </div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">Total Steps</div>
                  </div>
                  <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                    <div className="text-2xl font-bold text-slate-800 dark:text-slate-200">
                      {nodeCount}
                    </div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">Nodes</div>
                  </div>
                  <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                    <div className="text-2xl font-bold text-slate-800 dark:text-slate-200">
                      {edgeCount}
                    </div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">Connections</div>
                  </div>
                </div>
              </div>

              {/* Steps by Type */}
              {Object.keys(e2eFlowInfo.stats.stepsByType).length > 0 && (
                <div>
                  <h5 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Steps by Type
                  </h5>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(e2eFlowInfo.stats.stepsByType)
                      .filter(([, count]) => count && count > 0)
                      .map(([type, count]) => (
                        <span
                          key={type}
                          className="px-2 py-1 text-xs bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 rounded-full"
                        >
                          {STEP_TYPE_LABELS[type as FlowStepType] || type}: {count}
                        </span>
                      ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                <div className="text-2xl font-bold text-slate-800 dark:text-slate-200">
                  {nodeCount}
                </div>
                <div className="text-xs text-slate-500 dark:text-slate-400">Nodes</div>
              </div>
              <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                <div className="text-2xl font-bold text-slate-800 dark:text-slate-200">
                  {edgeCount}
                </div>
                <div className="text-xs text-slate-500 dark:text-slate-400">Connections</div>
              </div>
            </div>
          )}

          {/* Issue #404: E2E Flow Logic - How the diagram was traced */}
          {isE2EFlow && e2eFlowInfo?.flow && (
            <div>
              <h5 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Flow Tracing Logic
              </h5>
              <div className="space-y-3 text-sm text-slate-600 dark:text-slate-400">
                <div className="flex items-start gap-2">
                  <span className="text-blue-500 mt-0.5">1.</span>
                  <div>
                    <span className="font-medium">Trigger Detection:</span>{' '}
                    Started from {e2eFlowInfo.flow.triggerType === 'click' ? 'click handler' :
                                  e2eFlowInfo.flow.triggerType === 'submit' ? 'form submission' :
                                  e2eFlowInfo.flow.triggerType === 'navigate' ? 'navigation event' :
                                  e2eFlowInfo.flow.triggerType === 'load' ? 'page load' :
                                  e2eFlowInfo.flow.triggerType === 'event' ? 'custom event' :
                                  e2eFlowInfo.flow.triggerType} on{' '}
                    <code className="px-1 py-0.5 bg-slate-100 dark:bg-slate-800 rounded text-xs">
                      {e2eFlowInfo.flow.triggerName}
                    </code>
                    {e2eFlowInfo.flow.handlerName && (
                      <> → handler <code className="px-1 py-0.5 bg-slate-100 dark:bg-slate-800 rounded text-xs">
                        {e2eFlowInfo.flow.handlerName}
                      </code></>
                    )}
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-blue-500 mt-0.5">2.</span>
                  <div>
                    <span className="font-medium">API Call Tracing:</span>{' '}
                    Followed function calls to identify API requests (fetch, axios, etc.)
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-blue-500 mt-0.5">3.</span>
                  <div>
                    <span className="font-medium">Route Matching:</span>{' '}
                    Matched API endpoints to backend route handlers
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-blue-500 mt-0.5">4.</span>
                  <div>
                    <span className="font-medium">Database Detection:</span>{' '}
                    {e2eFlowInfo.flow.touchedTables.length > 0 ? (
                      <>Found {e2eFlowInfo.flow.touchedTables.length} table(s): {' '}
                        {e2eFlowInfo.flow.touchedTables.map((t, i) => (
                          <code key={t} className="px-1 py-0.5 bg-slate-100 dark:bg-slate-800 rounded text-xs">
                            {t}{i < e2eFlowInfo.flow!.touchedTables.length - 1 ? ', ' : ''}
                          </code>
                        ))}
                      </>
                    ) : (
                      'No database operations detected'
                    )}
                  </div>
                </div>
                {e2eFlowInfo.flow.touchedServices.length > 0 && (
                  <div className="flex items-start gap-2">
                    <span className="text-blue-500 mt-0.5">5.</span>
                    <div>
                      <span className="font-medium">External Services:</span>{' '}
                      {e2eFlowInfo.flow.touchedServices.join(', ')}
                    </div>
                  </div>
                )}
              </div>
              <div className="mt-3 p-2 bg-slate-50 dark:bg-slate-800 rounded-lg">
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${e2eFlowInfo.flow.complete ? 'bg-green-500' : 'bg-amber-500'}`} />
                  <span className="text-xs text-slate-600 dark:text-slate-400">
                    {e2eFlowInfo.flow.complete
                      ? 'Complete E2E flow - all connections traced'
                      : 'Partial flow - some connections could not be traced'}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Issue #404: E2E Flow Evidence - Source code references */}
          {isE2EFlow && e2eFlowInfo?.flow && e2eFlowInfo.flow.steps.length > 0 && (
            <div>
              <h5 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Evidence Sources
              </h5>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {e2eFlowInfo.flow.steps
                  .filter(step => step.sourceFile || step.evidence.length > 0)
                  .slice(0, 10) // Limit to first 10 for readability
                  .map((step) => (
                    <div
                      key={step.id}
                      className="p-2 bg-slate-50 dark:bg-slate-800 rounded text-xs"
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span className="px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded text-[10px]">
                          {STEP_TYPE_LABELS[step.type] || step.type}
                        </span>
                        <span className="text-slate-700 dark:text-slate-300 font-medium truncate">
                          {step.label}
                        </span>
                      </div>
                      {step.sourceFile && (
                        <div className="font-mono text-slate-500 dark:text-slate-500 truncate">
                          {step.sourceFile}{step.sourceLine ? `:${step.sourceLine}` : ''}
                        </div>
                      )}
                      {step.evidence.length > 0 && step.evidence[0].snippet && (
                        <div className="mt-1 p-1 bg-slate-100 dark:bg-slate-700 rounded font-mono text-[10px] text-slate-600 dark:text-slate-400 truncate">
                          {step.evidence[0].snippet}
                        </div>
                      )}
                    </div>
                  ))}
                {e2eFlowInfo.flow.steps.filter(s => s.sourceFile || s.evidence.length > 0).length > 10 && (
                  <div className="text-xs text-slate-500 dark:text-slate-500 text-center py-1">
                    + {e2eFlowInfo.flow.steps.filter(s => s.sourceFile || s.evidence.length > 0).length - 10} more steps
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Generation Info */}
          <div>
            <h5 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              How It Was Generated
            </h5>
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
                <span>{generationMethod}</span>
                {confidence !== null && confidence !== undefined && (
                  <span className="px-2 py-0.5 text-xs bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 rounded">
                    {Math.round(confidence * 100)}% confidence
                  </span>
                )}
              </div>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                {methodology}
              </p>
            </div>
          </div>

          {/* Issue #411: AI Enhancement Info */}
          {aiEnhancementInfo?.enhanced && (
            <div className="p-4 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-lg">✨</span>
                <h5 className="text-sm font-medium text-purple-800 dark:text-purple-300">
                  AI Enhancement Applied
                </h5>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <span className="text-purple-600 dark:text-purple-400">Depth:</span>
                  <span className="px-2 py-0.5 text-xs bg-purple-200 dark:bg-purple-800 text-purple-800 dark:text-purple-200 rounded">
                    {aiEnhancementInfo.depth === 'deep' ? 'Deep Analysis' : 'Standard'}
                  </span>
                </div>
                {aiEnhancementInfo.acceptedCount !== undefined && (
                  <div className="flex items-center gap-2 text-purple-600 dark:text-purple-400">
                    <span>Changes:</span>
                    <span className="text-green-600 dark:text-green-400">{aiEnhancementInfo.acceptedCount} accepted</span>
                    {aiEnhancementInfo.rejectedCount !== undefined && aiEnhancementInfo.rejectedCount > 0 && (
                      <>
                        <span>•</span>
                        <span className="text-red-600 dark:text-red-400">{aiEnhancementInfo.rejectedCount} rejected</span>
                      </>
                    )}
                  </div>
                )}
                {aiEnhancementInfo.enhancedAt && (
                  <div className="text-xs text-purple-500 dark:text-purple-500">
                    Enhanced: {new Date(aiEnhancementInfo.enhancedAt).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </div>
                )}
              </div>

              {/* Verification Loop Info */}
              {aiEnhancementInfo.verificationUsed && (
                <div className="mt-3 pt-3 border-t border-purple-200 dark:border-purple-700">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-amber-500">🔍</span>
                    <span className="text-sm font-medium text-amber-700 dark:text-amber-300">
                      AI Verification Loop Used
                    </span>
                    <span className="px-2 py-0.5 text-xs bg-amber-200 dark:bg-amber-800 text-amber-800 dark:text-amber-200 rounded-full">
                      {aiEnhancementInfo.questionsAsked || 0} question{(aiEnhancementInfo.questionsAsked || 0) !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <p className="text-xs text-amber-600 dark:text-amber-400 mb-2">
                    AI verified its suggestions by asking questions and checking source code.
                  </p>
                  {aiEnhancementInfo.verificationDetails && aiEnhancementInfo.verificationDetails.length > 0 && (
                    <div className="space-y-2 mt-2 max-h-40 overflow-auto">
                      {aiEnhancementInfo.verificationDetails.map((detail, i) => (
                        <div
                          key={i}
                          className="p-2 bg-white dark:bg-slate-800 rounded border border-amber-200 dark:border-amber-700"
                        >
                          <div className="flex items-start gap-2">
                            <span className="text-amber-500 font-medium text-xs mt-0.5">Q{i + 1}:</span>
                            <div className="flex-1">
                              <div className="text-xs text-slate-700 dark:text-slate-300">
                                {detail.question}
                              </div>
                              <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                                <span className="font-medium">Answer:</span> {detail.answer}
                              </div>
                              <div className="flex items-center gap-2 mt-1">
                                <span className="text-[10px] px-1.5 py-0.5 bg-slate-100 dark:bg-slate-700 text-slate-500 rounded">
                                  {detail.questionType}
                                </span>
                                <span className="text-[10px] text-slate-400">
                                  {Math.round(detail.confidence * 100)}% confidence
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Issue #411: Show what changed during verification */}
                  {aiEnhancementInfo.verificationRevisions && aiEnhancementInfo.verificationRevisions.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-amber-200 dark:border-amber-700">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-orange-500">✏️</span>
                        <span className="text-xs font-medium text-orange-700 dark:text-orange-400">
                          AI Revised Suggestions
                        </span>
                        <span className="px-2 py-0.5 text-[10px] bg-orange-200 dark:bg-orange-800 text-orange-800 dark:text-orange-200 rounded-full">
                          {aiEnhancementInfo.verificationRevisions.length} change{aiEnhancementInfo.verificationRevisions.length !== 1 ? 's' : ''}
                        </span>
                      </div>
                      <div className="space-y-2 max-h-32 overflow-auto">
                        {aiEnhancementInfo.verificationRevisions.map((revision, i) => (
                          <div
                            key={i}
                            className="p-2 bg-orange-50 dark:bg-orange-900/20 rounded border border-orange-200 dark:border-orange-700"
                          >
                            <div className="flex items-start gap-2">
                              <span className={`text-xs font-medium mt-0.5 ${
                                revision.type === 'missingStepRemoved' ? 'text-red-500' :
                                revision.type === 'missingStepAdded' ? 'text-green-500' :
                                'text-orange-500'
                              }`}>
                                {revision.type === 'missingStepRemoved' ? '−' :
                                 revision.type === 'missingStepAdded' ? '+' : '~'}
                              </span>
                              <div className="flex-1">
                                <div className="text-xs text-slate-700 dark:text-slate-300">
                                  {revision.type === 'name' && 'Name changed'}
                                  {revision.type === 'description' && 'Description changed'}
                                  {revision.type === 'missingStepRemoved' && 'Step removed'}
                                  {revision.type === 'missingStepAdded' && 'Step added'}
                                  {revision.type === 'stepLabelChanged' && 'Label updated'}
                                </div>
                                {revision.original && (
                                  <div className="text-[10px] text-red-500 dark:text-red-400 line-through truncate">
                                    {revision.original}
                                  </div>
                                )}
                                {revision.revised && (
                                  <div className="text-[10px] text-green-600 dark:text-green-400 truncate">
                                    {revision.revised}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Issue #424: View AI Details (Prompt/Response) */}
              {(aiEnhancementInfo.promptSent || aiEnhancementInfo.aiResponse) && (
                <div className="mt-3 pt-3 border-t border-purple-200 dark:border-purple-700">
                  <button
                    onClick={() => setShowAiDebug(!showAiDebug)}
                    className="flex items-center gap-2 text-xs font-medium text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-300 transition-colors"
                  >
                    <span>{showAiDebug ? '▼' : '▶'}</span>
                    <span>🔍 View AI Details</span>
                  </button>
                  {showAiDebug && (
                    <div className="mt-2 space-y-3">
                      {aiEnhancementInfo.promptSent && (
                        <div>
                          <div className="text-xs font-medium text-purple-700 dark:text-purple-300 mb-1">
                            Prompt Sent to AI
                          </div>
                          <div className="max-h-48 overflow-auto p-2 bg-white dark:bg-slate-800 rounded border border-purple-100 dark:border-purple-700">
                            <pre className="text-[10px] text-slate-600 dark:text-slate-400 whitespace-pre-wrap font-mono">
                              {aiEnhancementInfo.promptSent}
                            </pre>
                          </div>
                        </div>
                      )}
                      {aiEnhancementInfo.aiResponse && (
                        <div>
                          <div className="text-xs font-medium text-purple-700 dark:text-purple-300 mb-1">
                            Raw AI Response
                          </div>
                          <div className="max-h-48 overflow-auto p-2 bg-white dark:bg-slate-800 rounded border border-purple-100 dark:border-purple-700">
                            <pre className="text-[10px] text-slate-600 dark:text-slate-400 whitespace-pre-wrap font-mono">
                              {aiEnhancementInfo.aiResponse}
                            </pre>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Data Sources */}
          {dataSources.length > 0 && (
            <div>
              <h5 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Data Sources
              </h5>
              <ul className="space-y-1">
                {dataSources.map((source, index) => (
                  <li
                    key={index}
                    className="flex items-start gap-2 text-sm text-slate-600 dark:text-slate-400"
                  >
                    <span className="text-slate-400 mt-0.5">
                      {source.type === 'file' && '📄'}
                      {source.type === 'pattern' && '🔍'}
                      {source.type === 'api' && '🌐'}
                      {source.type === 'config' && '⚙️'}
                      {source.type === 'ast' && '🌳'}
                    </span>
                    <span>
                      {source.path && (
                        <code className="text-xs bg-slate-100 dark:bg-slate-800 px-1 py-0.5 rounded mr-1">
                          {source.path}
                        </code>
                      )}
                      {source.description}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Interpretation Guide */}
          {interpretationGuide && (
            <div>
              <h5 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                How to Read This Diagram
              </h5>
              <div className="text-sm text-slate-600 dark:text-slate-400 whitespace-pre-line">
                {interpretationGuide}
              </div>
            </div>
          )}

          {/* Legend */}
          {legend.length > 0 && (
            <div>
              <h5 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Legend
              </h5>
              <div className="space-y-2">
                {legend.map((item, index) => (
                  <div key={index} className="flex items-start gap-2 text-sm">
                    <span className="text-lg">{item.symbol}</span>
                    <div>
                      <span className="text-slate-700 dark:text-slate-300">{item.meaning}</span>
                      {item.example && (
                        <span className="text-slate-500 dark:text-slate-500 ml-1">
                          ({item.example})
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Timestamps */}
          {(createdAt || updatedAt) && (
            <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
              <div className="space-y-1 text-xs text-slate-500 dark:text-slate-500">
                {createdAt && (
                  <div>Created: {formatDate(createdAt)}</div>
                )}
                {updatedAt && (
                  <div>Last updated: {formatDate(updatedAt)}</div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-700 flex gap-2">
          <button
            onClick={() => {
              const text = `${staticDescription?.title || 'Diagram'}\n\n${description}\n\nMethodology: ${methodology}`;
              navigator.clipboard.writeText(text);
            }}
            className="flex-1 px-3 py-2 text-sm font-medium bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
            </svg>
            Copy Description
          </button>
        </div>
      </div>
    </>
  );
}

export default DiagramInfoPanel;
